import React from 'react'
import { MarketViewScores } from '../../services/api'
import { describeScore } from '../../lib/scoreDisplay'

interface ScoreRadarChartProps {
  scores: MarketViewScores
  size?: number
}

const ScoreRadarChart: React.FC<ScoreRadarChartProps> = ({
  scores,
  size = 120,
}) => {
  const center = size / 2
  const radius = (size / 2) - 15

  // Five dimensions grounded in real scorecard evaluations. A dimension the
  // backend did not measure has no coordinate - it is drawn as a dimmed axis
  // with its state spelled out, never as a point at 50 (DEV-584).
  const dimensions = [
    { key: 'need_fit', label: 'Basic Req', score: describeScore(scores.need_fit) },
    { key: 'technical', label: 'Technical', score: describeScore(scores.technical) },
    { key: 'manufacturing', label: 'Prod', score: describeScore(scores.manufacturing) },
    { key: 'vc_metrics', label: 'Stability', score: describeScore(scores.vc_metrics) },
    { key: 'dod_partnership', label: 'DoW', score: describeScore(scores.dod_partnership) },
  ]

  // Calculate points on the radar
  const angleStep = (2 * Math.PI) / dimensions.length
  const startAngle = -Math.PI / 2 // Start from top

  const getPoint = (value: number, index: number, maxRadius: number = radius) => {
    const angle = startAngle + index * angleStep
    const r = (value / 100) * maxRadius
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle),
    }
  }

  const measured = dimensions
    .map((dim, i) => ({ ...dim, index: i }))
    .filter((dim) => dim.score.isScored)

  // A shape needs three vertices. With one or two measured axes the dots alone
  // are the honest rendering - a two-point "polygon" reads as a collapsed score.
  const scorePoints =
    measured.length >= 3
      ? measured
          .map((dim) => getPoint(dim.score.value as number, dim.index))
          .map((p) => `${p.x},${p.y}`)
          .join(' ')
      : null

  // Generate axis lines
  const axisLines = dimensions.map((_, i) => {
    const endpoint = getPoint(100, i)
    return `M${center},${center} L${endpoint.x},${endpoint.y}`
  })

  // Generate concentric circles for grid
  const gridCircles = [25, 50, 75, 100].map((level) => {
    const gridPoints = dimensions
      .map((_, i) => getPoint(level, i))
      .map((p) => `${p.x},${p.y}`)
      .join(' ')
    return gridPoints
  })

  // Get color based on value
  const getScoreColor = (value: number) => {
    if (value >= 80) return 'var(--success)'
    if (value >= 60) return 'var(--primary)'
    if (value >= 40) return 'var(--warning)'
    return 'var(--destructive)'
  }

  return (
    <svg width={size} height={size} className="overflow-visible">
      {/* Grid circles */}
      {gridCircles.map((points, i) => (
        <polygon
          key={i}
          points={points}
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
          className="text-muted-foreground"
          opacity={0.4}
        />
      ))}

      {/* Axis lines - dimmed where there is nothing to plot */}
      {axisLines.map((d, i) => (
        <path
          key={i}
          d={d}
          stroke="currentColor"
          strokeWidth="1"
          className="text-muted-foreground"
          opacity={dimensions[i].score.isScored ? 0.4 : 0.15}
        />
      ))}

      {/* Score polygon */}
      {scorePoints && (
        <polygon
          points={scorePoints}
          fill={getScoreColor(scores.overall)}
          fillOpacity={0.3}
          stroke={getScoreColor(scores.overall)}
          strokeWidth="2"
        />
      )}

      {/* Score dots */}
      {measured.map((dim) => {
        const point = getPoint(dim.score.value as number, dim.index)
        return (
          <circle
            key={dim.key}
            cx={point.x}
            cy={point.y}
            r={3}
            fill={getScoreColor(dim.score.value as number)}
          />
        )
      })}

      {/* Labels with scores on same line */}
      {dimensions.map((dim, i) => {
        const labelPoint = getPoint(100, i, radius + 16)
        const anchor =
          i === 0 ? 'middle' :
          i === 1 || i === 2 ? 'start' :
          'end'

        return (
          <text
            key={dim.key}
            x={labelPoint.x}
            y={labelPoint.y}
            textAnchor={anchor}
            dominantBaseline="middle"
            className="text-[8px]"
          >
            <title>{dim.score.explanation}</title>
            <tspan className="fill-muted-foreground">{dim.label}</tspan>
            {dim.score.isScored ? (
              <tspan
                dx="4"
                fill={getScoreColor(dim.score.value as number)}
                className="font-semibold"
              >
                {dim.score.text}
              </tspan>
            ) : (
              // Muted, same size, no colour: an intentional state, not an alert
              // and not a loading skeleton.
              <tspan dx="4" className="fill-muted-foreground">
                {dim.score.text}
              </tspan>
            )}
          </text>
        )
      })}
    </svg>
  )
}

export default ScoreRadarChart
